import React, { forwardRef, useState } from 'react'
import {
  Sparkle,
  X,
  ArrowRight,
  ArrowLeft,
  ArrowClockwise,
  CopySimple,
} from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import api from '../../api/api'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import Loader from '../Loader/Loader'
import { getProperRoleForChat } from '../../helpers/helpers'

import {
  getOptions,
  getRewriteOptions,
  getAdjustToneOptions,
  getLimitedOptions,
  MAX_SMS_LENGTH,
  MAX_CHARACTERS_LENGTH,
  getResultInstructionIcon,
  getResultInstructionTitle,
} from './helpers'

const NUM_SUGGESTIONS_LEFT = 5

const ImproveWithAIWindow = forwardRef(
  (
    {
      onClose,
      textInputRef,
      lastResidentMessage,
      sendMessageAsSMS,
      userRole,
      buildingId,
      onInsertResult = () => {},
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation(['improve-with-ai'])
    const OPTIONS = getOptions(t)
    const REWRITE_OPTIONS = getRewriteOptions(t)
    const ADJUST_TONE_OPTIONS = getAdjustToneOptions(t)
    const LIMITED_OPTIONS = getLimitedOptions(t)
    const [viewMore, setViewMore] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [results, setResults] = useState([])
    const [limitReached, setLimitReached] = useState(false)
    const [suggestionsLeft, setSuggestionsLeft] = useState(0)
    const { displaySnackbar } = useSnackbar()

    const applyInstruction = async (instruction, inputText) => {
      try {
        const residentMessage = lastResidentMessage ? lastResidentMessage.text : null

        if (!inputText) {
          inputText =
            results.length > 0 ? results[results.length - 1].updated : textInputRef.current.value
        }

        const requestBody = {
          buildingId,
          chatRole: getProperRoleForChat(userRole),
          charactersLimit: sendMessageAsSMS ? MAX_SMS_LENGTH : MAX_CHARACTERS_LENGTH,
          inputText,
          residentMessage,
          instruction,
        }

        setIsLoading(true)
        const response = await api.getImprovedWithAIWriter(requestBody)
        setSuggestionsLeft(response.data.suggestionsLeft)
        setResults((prevResults) => [...prevResults, response.data])
        setIsLoading(false)
        setViewMore(false)
      } catch (error) {
        if (error.response.data.message === 'Limit exceeded') {
          setLimitReached(true)
        } else {
          displaySnackbar(t('improve-with-ai:toasts.improveError'), true)
        }
      } finally {
        setIsLoading(false)
      }
    }

    const onCopyResult = (result) => {
      navigator.clipboard.writeText(result.updated)
      displaySnackbar(t('improve-with-ai:toasts.copiedToClipboard'), false)
    }

    const onInsert = (result) => {
      textInputRef.current.value = result.updated
      onInsertResult(result.updated)
    }

    return (
      <div className="improve-with-ai--window" {...props} ref={ref}>
        <div className="improve-with-ai--window-header">
          <div className="improve-with-ai--window-header-title-container">
            <div className="improve-with-ai--window-header-title">
              <Sparkle size={24} />
              <span>{t('improve-with-ai:shared.title')}</span>
            </div>
            {limitReached ? (
              <div className="improve-with-ai--window-header-limit-reached">
                <span>{t('improve-with-ai:window.limitReachedMessage')}</span>
              </div>
            ) : suggestionsLeft && Math.max(suggestionsLeft, 1) <= NUM_SUGGESTIONS_LEFT ? (
              <div className="improve-with-ai--window-header-suggestions-left">
                <span>{t('improve-with-ai:window.suggestionsLeft', { num: suggestionsLeft })}</span>
              </div>
            ) : null}
          </div>
          <div className="improve-with-ai--window-header-close" onClick={onClose}>
            <X size={18} />
          </div>
        </div>
        {isLoading ? (
          <div className="improve-with-ai--window-loader">
            <Loader />
            <span>{t('improve-with-ai:window.loadingMessage')}</span>
          </div>
        ) : (
          <React.Fragment>
            <div
              className={`improve-with-ai--window-content ${
                limitReached ? 'improve-with-ai--window-content-limit-reached' : ''
              }`}
            >
              {viewMore ? (
                <React.Fragment>
                  <div
                    className="improve-with-ai--window-content-back"
                    onClick={() => setViewMore(false)}
                  >
                    <ArrowLeft size={14} weight="light" />
                    <span>{t('improve-with-ai:window.backButton')}</span>
                  </div>
                  <div className="improve-with-ai--window-content-title">
                    <span>{t('improve-with-ai:window.rewriteSection')}</span>
                  </div>
                  {REWRITE_OPTIONS.map((option, index) => (
                    <div
                      className="improve-with-ai--window-content-option"
                      key={`improve-with-ai-option-${index}`}
                      onClick={() => applyInstruction(option.instruction)}
                    >
                      <img src={option.icon} alt={option.title} />
                      <span>{option.title}</span>
                    </div>
                  ))}
                  <div className="improve-with-ai--window-content-title">
                    <span>{t('improve-with-ai:window.adjustToneSection')}</span>
                  </div>
                  {ADJUST_TONE_OPTIONS.map((option, index) => (
                    <div
                      className="improve-with-ai--window-content-option"
                      key={`improve-with-ai-option-${index}`}
                      onClick={() => applyInstruction(option.instruction)}
                    >
                      <img src={option.icon} alt={option.title} />
                      <span>{option.title}</span>
                    </div>
                  ))}
                </React.Fragment>
              ) : results && results.length > 0 ? (
                <React.Fragment>
                  <div className="improve-with-ai--window-content-results">
                    {results.map((result, index) => (
                      <div
                        key={`improve-with-ai-result-${index}`}
                        className="improve-with-ai--window-content-result"
                      >
                        <div className="improve-with-ai--window-content-result-instruction">
                          <img src={getResultInstructionIcon(result.instruction, t)} />
                          <span>{getResultInstructionTitle(result.instruction, t)}</span>
                        </div>
                        <span>{result.updated}</span>
                        <div className="improve-with-ai--window-content-result-actions">
                          <div>
                            <button
                              className="button button-primary"
                              onClick={() => onInsert(result)}
                            >
                              {t('improve-with-ai:result.insertButton')}
                            </button>
                            <button
                              className="button button-lightgray"
                              onClick={() => applyInstruction(result.instruction, result.original)}
                            >
                              <ArrowClockwise size={20} />
                              {t('improve-with-ai:result.retryButton')}
                            </button>
                          </div>
                          <div
                            className="improve-with-ai--window-content-result-actions-copy"
                            onClick={() => onCopyResult(result)}
                          >
                            <CopySimple size={20} weight="light" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="improve-with-ai--window-content-result-options">
                    {LIMITED_OPTIONS.map((option, index) => (
                      <div
                        className="improve-with-ai--window-content-option"
                        key={`improve-with-ai-option-${index}`}
                        onClick={() => applyInstruction(option.instruction)}
                      >
                        <img src={option.icon} alt={option.title} />
                        <span>{option.title}</span>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <div className="improve-with-ai--window-content-title">
                    <span>{t('improve-with-ai:options.prompt')}</span>
                  </div>
                  {OPTIONS.map((option, index) => (
                    <div
                      className="improve-with-ai--window-content-option"
                      key={`improve-with-ai-option-${index}`}
                      onClick={() => applyInstruction(option.instruction)}
                    >
                      <img src={option.icon} alt={option.title} />
                      <span>{option.title}</span>
                    </div>
                  ))}
                </React.Fragment>
              )}
            </div>
            {!viewMore && !limitReached && (
              <div className="improve-with-ai--window-content-footer">
                <div
                  className="improve-with-ai--window-content-footer-button"
                  onClick={() => setViewMore(!viewMore)}
                >
                  <span>{t('improve-with-ai:window.viewMoreButton')}</span>
                  <ArrowRight size={24} weight="light" />
                </div>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    )
  },
)

ImproveWithAIWindow.displayName = 'ImproveWithAIWindow'

ImproveWithAIWindow.propTypes = {
  onClose: PropTypes.func.isRequired,
  textInputRef: PropTypes.object,
  lastResidentMessage: PropTypes.object,
  sendMessageAsSMS: PropTypes.bool,
  userRole: PropTypes.string,
  buildingId: PropTypes.number,
  onInsertResult: PropTypes.func,
}

export default ImproveWithAIWindow
